/**
 * React hooks around `WsClient` and `/api/timeseries`.
 *
 * Exports:
 *   - `useConnectionStatus()` → current WS connection state ('open' | …)
 *   - `useTimeSeriesSubscription(deviceId, sensorId)` → latest reading
 *   - `useHistoricalData(deviceId, sensorId, from, to, maxPoints?)` →
 *     `{ points, loading, error, refresh }`
 *
 * The hooks intentionally avoid `useSyncExternalStore` so we can keep
 * the test surface a plain Vitest + React-Testing-Library setup.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { readAccessKey } from '../lib/agent-fetch';
import { loadPublicEnv } from '../lib/env';
import {
  fetchTimeSeries,
  type TimeSeriesPoint,
} from '../lib/timeseries-api';
import {
  getWsClient,
  type WsClient,
  type WsConnectionStatus,
} from '../lib/wsClient';

/**
 * Convert the resolved agent base URL → `ws(s)://host:port/ws`. An
 * empty base means same-origin (nginx proxy / Vite proxy), so derive
 * the socket URL from `window.location`.
 */
function defaultWsUrl(): string {
  const httpUrl = loadPublicEnv().agentDevUrl;
  let wsUrl: string;
  if (httpUrl === '') {
    if (typeof window !== 'undefined' && window.location) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${proto}//${window.location.host}/ws`;
    } else {
      wsUrl = '/ws';
    }
  } else if (httpUrl.startsWith('https://')) {
    wsUrl = httpUrl.replace(/^https:\/\//, 'wss://') + '/ws';
  } else if (httpUrl.startsWith('http://')) {
    wsUrl = httpUrl.replace(/^http:\/\//, 'ws://') + '/ws';
  } else {
    // Already a ws(s) URL — append `/ws` if missing.
    wsUrl = httpUrl.endsWith('/ws') ? httpUrl : `${httpUrl}/ws`;
  }
  // Public-access build: browsers can't set WS headers, so the passcode
  // rides as a query param (docs/SPEC-public-access.md).
  const key = readAccessKey();
  return key ? `${wsUrl}?key=${encodeURIComponent(key)}` : wsUrl;
}

function ensureClient(): WsClient {
  return getWsClient(defaultWsUrl());
}

/** Subscribe to the WS connection status. Defaults to 'closed' until the first push. */
export function useConnectionStatus(): WsConnectionStatus {
  const [status, setStatus] = useState<WsConnectionStatus>('closed');
  useEffect(() => {
    const client = ensureClient();
    const off = client.subscribeStatus((s) => setStatus(s));
    return off;
  }, []);
  return status;
}

export interface UseTimeSeriesSubscriptionResult {
  /** Most-recent reading received over the WebSocket, or `null` if none yet. */
  latest: TimeSeriesPoint | null;
  /** Connection status for convenience (mirrors `useConnectionStatus`). */
  status: WsConnectionStatus;
}

/**
 * Subscribe to live `(deviceId, sensorId)` readings. Unsubscribes on
 * unmount and when `deviceId`/`sensorId` change.
 */
export function useTimeSeriesSubscription(
  deviceId: string,
  sensorId: string,
): UseTimeSeriesSubscriptionResult {
  const [latest, setLatest] = useState<TimeSeriesPoint | null>(null);
  const [status, setStatus] = useState<WsConnectionStatus>('closed');
  useEffect(() => {
    const client = ensureClient();
    setLatest(null);
    const offStatus = client.subscribeStatus(setStatus);
    const offReading = client.subscribeReading(deviceId, sensorId, (msg) => {
      setLatest({ ts: msg.ts, value: msg.value });
    });
    return () => {
      offReading();
      offStatus();
    };
  }, [deviceId, sensorId]);
  return { latest, status };
}

export interface UseHistoricalDataResult {
  points: TimeSeriesPoint[];
  loading: boolean;
  error: string | null;
  /** Re-fetch with the current arguments. */
  refresh: () => Promise<void>;
}

/**
 * Fetch the agent's `/api/timeseries` window once on mount and any time
 * the inputs change. Does *not* subscribe to live updates — combine with
 * `useTimeSeriesSubscription` if you want both a backfilled window and
 * the latest tick.
 */
export function useHistoricalData(
  deviceId: string,
  sensorId: string,
  from: number,
  to: number,
  maxPoints?: number,
): UseHistoricalDataResult {
  const [points, setPoints] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }
    const r = await fetchTimeSeries({
      deviceId,
      sensorId,
      from,
      to,
      ...(maxPoints !== undefined ? { maxPoints } : {}),
    });
    if (!mountedRef.current) return;
    if (!r.ok) {
      setError(r.error);
      setPoints([]);
    } else {
      setPoints(r.value);
    }
    setLoading(false);
  }, [deviceId, sensorId, from, to, maxPoints]);

  useEffect(() => {
    void load();
  }, [load]);

  return { points, loading, error, refresh: load };
}